import React from "react";
import { useTranslation } from "react-i18next";
import { StepFieldConfig } from "../config/demoSites";

interface FormTableProps {
    field: StepFieldConfig;
    value: any[];
    onChange: (value: any[]) => void;
}

const FormTable: React.FC<FormTableProps> = ({ field, value, onChange }) => {
    // onChange will be used for future edit functionality
    onChange;
    const { t } = useTranslation();
    const tableConfig = field.tableConfig;

    if (!tableConfig) {
        return <div className="text-red-500">Table configuration missing</div>;
    }

    // Default empty array if value is not set
    const rows = Array.isArray(value) ? value : [];

    const renderCell = (row: any, column: (typeof tableConfig.columns)[0]) => {
        const cellValue = row[column.key];

        if (column.cellType === "status" && column.statusConfig) {
            const statusOption = column.statusConfig.options.find(opt => opt.value === cellValue);
            if (statusOption) {
                return (
                    <span className={`inline-block rounded-full px-2 py-1 text-xs font-semibold ${statusOption.color}`}>
                        {t(statusOption.labelKey, { defaultValue: cellValue }).toString()}
                    </span>
                );
            }
        }

        return cellValue;
    };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {tableConfig.columns.map(column => (
                            <th
                                key={column.key}
                                scope="col"
                                className={`px-6 py-3 text-${column.align || "left"} text-xs font-medium uppercase tracking-wider text-gray-500`}
                                style={{ width: column.width }}
                            >
                                {t(column.headerKey, { defaultValue: column.key }).toString()}
                            </th>
                        ))}
                        {tableConfig.rowActions && (
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {tableConfig.columns.map(column => (
                                <td key={column.key} className={`whitespace-nowrap px-6 py-4 text-${column.align || "left"} text-sm text-gray-500`}>
                                    {renderCell(row, column)}
                                </td>
                            ))}
                            {tableConfig.rowActions && (
                                <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                    {tableConfig.rowActions.map(action => (
                                        <button
                                            key={action.action}
                                            className="ml-2 text-indigo-600 hover:text-indigo-900"
                                            onClick={() => {
                                                // Handle action based on action.action
                                                console.log(`Action ${action.action} on row ${rowIndex}`);
                                            }}
                                        >
                                            {t(action.labelKey, { defaultValue: action.action }).toString()}
                                        </button>
                                    ))}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default FormTable;
